import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Users2, CheckCircle2, XCircle, Loader2, Sparkles, FileText,
  Send, RotateCcw, Wallet, Download, Trash2, Plus, ChevronDown, ChevronRight,
  AlertTriangle, ShieldAlert, Clock,
} from "lucide-react";
import api, { getUser, downloadAuthedFile } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const DAYS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

// ------ Small helpers ------
function Precondition({ ok, label, testid }) {
  return (
    <div className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-semibold px-3 py-1.5 rounded-full border ${
      ok ? "bg-[hsl(var(--success-bg))] border-[hsl(var(--success))]/40 text-[hsl(var(--success))]"
         : "bg-muted border-border text-muted-foreground"
    }`} data-testid={testid}>
      {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5 opacity-60" />}
      {label}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", testid, multiline, rows = 2 }) {
  const inp = "w-full bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary";
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-1">{label}</div>
      {multiline ? (
        <textarea rows={rows} value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder} className={inp}
          data-testid={testid} />
      ) : (
        <input type={type} value={value ?? ""}
          onChange={(e) => onChange(type === "number" ? Number(e.target.value) : e.target.value)}
          placeholder={placeholder} className={inp}
          data-testid={testid} />
      )}
    </label>
  );
}

function Section({ title, children, open, onToggle, testid }) {
  return (
    <div className="border border-border rounded-xl bg-card overflow-hidden" data-testid={testid}>
      <button onClick={onToggle} className="w-full flex items-center justify-between px-4 py-3 bg-muted/40 hover:bg-muted text-left">
        <div className="font-ui text-sm text-foreground uppercase tracking-[0.15em]">{title}</div>
        {open ? <ChevronDown className="w-4 h-4 text-foreground" /> : <ChevronRight className="w-4 h-4 text-foreground" />}
      </button>
      {open && <div className="p-4 border-t border-border">{children}</div>}
    </div>
  );
}

// ================================================================
// Main panel
// ================================================================
export default function RecruitmentPanel({ lead, onChange }) {
  const user = getUser();
  // 26/02/2026 — Post-restructure a requisição de recrutamento também
  // pode ser gerada pelo Consultor de Cuidados (que herdou várias
  // atribuições do Gestor pré-04/08/2026). Sem esta linha, o endpoint
  // POST /leads/{id}/recruitment-requisition devolvia 403 e o botão
  // "Gerar" no painel ficava em disabled — utilizador reportou o bug.
  const canOperate = ["admin", "gestor", "consultor"].includes(user?.role);

  const deposit = lead?.deposit || {};
  const req = lead?.recruitment_requisition || null;

  const preAccepted   = Boolean(lead?.acceptance?.accepted);
  const preContract   = Boolean(lead?.contract_data?.completed);
  const preDeposit    = Boolean(deposit.kickstart_paid);
  const readyToGen    = preAccepted && preContract && preDeposit;

  const [formOpen, setFormOpen] = useState(false);
  const [prefilling, setPrefilling] = useState(false);
  const [savingReq, setSavingReq] = useState(false);
  const [sending, setSending] = useState(false);
  const [openSections, setOpenSections] = useState({
    general: true, profile: true, service: true, client: false, values: true, obs: false,
  });
  const [form, setForm] = useState(null);   // the editable requisition
  const [overrunState, setOverrunState] = useState(null);  // {current_overrun, latest_authorization, approver_email}
  const [overrunReason, setOverrunReason] = useState("");
  const [savingReason, setSavingReason] = useState(false);

  const fetchOverrunState = async () => {
    if (!lead?.id) return;
    try {
      const r = await api.get(`/leads/${lead.id}/recruitment-overrun`);
      setOverrunState(r.data || null);
      if (r.data?.latest_authorization?.reason && !overrunReason) {
        setOverrunReason(r.data.latest_authorization.reason);
      }
    } catch (e) {
      // Non-fatal — banner just won't show.
    }
    try {
      const r = await api.get(`/leads/${lead.id}/recruitment-margin-authorization`);
      setMarginAuthState(r.data || null);
    } catch (e) {
      // Non-fatal.
    }
  };

  // 27/02/2026 — Margin (33% floor) authorization request state.
  const [marginAuthState, setMarginAuthState] = useState(null);
  const [showMarginRequest, setShowMarginRequest] = useState(false);
  const [marginReason, setMarginReason] = useState("");
  const [submittingMarginReq, setSubmittingMarginReq] = useState(false);

  const submitMarginAuthRequest = async () => {
    if (!marginReason.trim()) {
      toast.error("Descreve o motivo do pedido antes de enviar.");
      return;
    }
    const revenue = Number(form.values?.client_monthly_value || 0);
    const helpersL = form.values?.helpers || [];
    const afCost = helpersL.reduce(
      (acc, h) => acc + Number(h.monthly_hours || 0) * Number(h.hourly_rate || 0),
      0,
    );
    setSubmittingMarginReq(true);
    try {
      await api.post(`/leads/${lead.id}/recruitment-margin-authorization`, {
        revenue: Number(revenue.toFixed(2)),
        af_cost: Number(afCost.toFixed(2)),
        reason: marginReason.trim(),
      });
      toast.success("Pedido enviado a caring@caring.pt. Receberás a decisão por email.");
      setShowMarginRequest(false);
      setMarginReason("");
      fetchOverrunState();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao enviar pedido.");
    } finally { setSubmittingMarginReq(false); }
  };

  useEffect(() => { fetchOverrunState(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [lead?.id, req?.updated_at]);

  useEffect(() => {
    if (req) setForm(req);
  }, [req]);

  // ---------- prefill ----------
  const openForm = async () => {
    setFormOpen(true);
    if (!form) {
      setPrefilling(true);
      try {
        const r = await api.post(`/leads/${lead.id}/recruitment-requisition/prefill`);
        setForm(r.data);
        toast.success("Requisição pré-preenchida com dados do lead + sugestões IA.");
      } catch (e) {
        toast.error(e.response?.data?.detail || "Erro no pré-preenchimento.");
      } finally {
        setPrefilling(false);
      }
    }
  };

  const regenerateAI = async () => {
    setPrefilling(true);
    try {
      const r = await api.post(`/leads/${lead.id}/recruitment-requisition/prefill`);
      setForm((old) => ({ ...r.data, id: old?.id }));
      toast.success("Novo pré-preenchimento aplicado.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao regenerar");
    } finally {
      setPrefilling(false);
    }
  };

  const saveReq = async () => {
    setSavingReq(true);
    try {
      const r = await api.post(`/leads/${lead.id}/recruitment-requisition`, form);
      setForm(r.data.recruitment_requisition);
      toast.success("Requisição guardada.");
      if (r.data?.overrun_request) {
        toast.warning(
          "Custo AFs acima do autorizado — pedido de aprovação enviado a "
          + (overrunState?.approver_email || "rui.pinto@caring.pt") + ".",
        );
      }
      await fetchOverrunState();
      onChange && onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar");
    } finally {
      setSavingReq(false);
    }
  };

  const saveOverrunReason = async () => {
    if (!overrunReason.trim()) return;
    setSavingReason(true);
    try {
      await api.post(`/leads/${lead.id}/recruitment-overrun/reason`, {
        reason: overrunReason.trim(),
      });
      toast.success("Motivo actualizado no pedido.");
      await fetchOverrunState();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar motivo.");
    } finally { setSavingReason(false); }
  };

  const sendToHR = async () => {
    if (!req) {
      toast.error("Guarde a requisição antes de enviar.");
      return;
    }
    setSending(true);
    try {
      const r = await api.post(`/leads/${lead.id}/recruitment-requisition/send`);
      const est = r.data?.email_status?.status;
      if (est === "sent") toast.success("Requisição enviada ao RH.");
      else if (est === "mocked") toast.info("Requisição registada (email em modo simulado).");
      else if (est === "error") toast.warning("Erro no envio real — verifique o log de emails.");
      await fetchOverrunState();
      onChange && onChange();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === "object" && detail?.code === "overrun_authorization_required") {
        toast.error(detail.message || "Aguarda aprovação de rui.pinto@caring.pt.");
      } else {
        toast.error((typeof detail === "string" ? detail : null) || "Erro ao enviar");
      }
    } finally { setSending(false); }
  };

  const pdfHref = useMemo(() => {
    // Kept as href fallback for right-click "open in new tab", but the primary
    // download path uses `downloadAuthedFile` so the Bearer token is attached.
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    return `${base}/api/leads/${lead.id}/recruitment-requisition/pdf`;
  }, [lead?.id]);

  const openPdf = async (e) => {
    e.preventDefault();
    const client = (lead?.qualification?.cared_person?.name || lead?.qualification?.contact?.name || "cliente").replace(/\s+/g, "_");
    try {
      await downloadAuthedFile(
        `/leads/${lead.id}/recruitment-requisition/pdf`,
        `Requisicao-Recrutamento-${client}.pdf`,
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível abrir o PDF.");
    }
  };

  // Only show the panel if the proposal has been accepted (start of the pipeline)
  if (!preAccepted) return null;

  // ---------- form helpers ----------
  const set = (path, value) => {
    setForm((f) => {
      const next = structuredClone(f || {});
      let cur = next;
      const parts = path.split(".");
      for (let i = 0; i < parts.length - 1; i++) {
        cur[parts[i]] = cur[parts[i]] || {};
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const scheduleAt = (i, key, value) => {
    setForm((f) => {
      const next = structuredClone(f || {});
      next.service_organization = next.service_organization || {};
      const arr = next.service_organization.schedule || [];
      while (arr.length <= i) arr.push({ day: DAYS[arr.length], hours: "", shift: "Dia", active: true });
      arr[i] = { ...arr[i], [key]: value };
      next.service_organization.schedule = arr;
      return next;
    });
  };

  const addMed = () => {
    setForm((f) => {
      const next = structuredClone(f || {});
      next.values = next.values || {};
      next.values.medication_schedule = [...(next.values.medication_schedule || []),
                                           { dose: "", hour: "", who: "", obs: "" }];
      return next;
    });
  };
  const removeMed = (i) => {
    setForm((f) => {
      const next = structuredClone(f || {});
      next.values.medication_schedule.splice(i, 1);
      return next;
    });
  };
  const setMed = (i, key, value) => {
    setForm((f) => {
      const next = structuredClone(f || {});
      next.values.medication_schedule[i] = { ...next.values.medication_schedule[i], [key]: value };
      return next;
    });
  };

  // ---------- helpers allocation ----------
  const addHelper = () => {
    setForm((f) => {
      const next = structuredClone(f || {});
      next.values = next.values || {};
      const arr = next.values.helpers || [];
      if (arr.length >= 4) return f;
      arr.push({ role: "AF", weekly_hours: 0, monthly_hours: 0, hourly_rate: 0, shift: "Dia", days: "", notes: "" });
      next.values.helpers = arr;
      return next;
    });
  };
  const removeHelper = (i) => {
    setForm((f) => {
      const next = structuredClone(f || {});
      (next.values.helpers || []).splice(i, 1);
      return next;
    });
  };
  const setHelper = (i, key, value) => {
    setForm((f) => {
      const next = structuredClone(f || {});
      next.values.helpers[i] = { ...next.values.helpers[i], [key]: value };
      return next;
    });
  };
  const distributeEvenly = () => {
    setForm((f) => {
      const next = structuredClone(f || {});
      const target = Number(next.values?.client_monthly_hours || 0);
      const helpers = next.values?.helpers || [];
      if (!helpers.length || target <= 0) return f;
      const share = Math.round((target / helpers.length) * 10) / 10;
      let remainder = Math.round((target - share * (helpers.length - 1)) * 10) / 10;
      // 27/02/2026 — Also auto-fill €/h so the total AF cost aligns with the
      // authorized ceiling. Formula: hourly_rate = af_monthly_cost / total_hours
      // (same for every helper, 2 decimals). User can still tweak per helper.
      const afCost = Number(lead?.estimate?.af_monthly_cost || 0);
      const autoRate = target > 0 && afCost > 0
        ? Math.round((afCost / target) * 100) / 100
        : null;
      for (let i = 0; i < helpers.length; i++) {
        const monthly = i === helpers.length - 1 ? remainder : share;
        helpers[i].monthly_hours = monthly;
        // 27/02/2026 — mantém `weekly_hours` sincronizado para o UI mostrar
        // o input semanal com o valor correcto.
        helpers[i].weekly_hours = Number((monthly / 4.33).toFixed(2));
        if (autoRate != null) {
          helpers[i].hourly_rate = autoRate;
        }
      }
      next.values.helpers = helpers;
      return next;
    });
  };

  // 27/02/2026 — Auto-fill every helper's €/h from the authorized AF cost
  // ceiling. Used both by "Distribuir uniformemente" and by the standalone
  // "Preencher €/h auto" button. Rounded to 2 decimals.
  const autofillHourlyRates = () => {
    setForm((f) => {
      const next = structuredClone(f || {});
      const target = Number(next.values?.client_monthly_hours || 0);
      const afCost = Number(lead?.estimate?.af_monthly_cost || 0);
      if (target <= 0 || afCost <= 0) return f;
      const rate = Math.round((afCost / target) * 100) / 100;
      const helpers = next.values?.helpers || [];
      helpers.forEach((h) => { h.hourly_rate = rate; });
      next.values.helpers = helpers;
      return next;
    });
    toast.success("€/h preenchido automaticamente com base no custo AFs autorizado.");
  };

  // Deriva as horas mensais correctas: fonte de verdade = **opção primária
  // da estimativa** (o que o consultor negociou). O formulário de qualificação
  // é intake inicial (frequentemente com defaults 5×8) e não deve ser usado
  // aqui. "Interna" no service_types força 24×7 como safety net.
  const _deriveExpectedClientHours = () => {
    const est = lead?.estimate || {};
    const qs = lead?.qualification?.service || {};
    const types = (qs.service_types || []).map((t) => String(t || "").toLowerCase());
    const isInterna = types.some((t) => t.includes("interna") || (t.includes("24") && t.includes("hora")));

    // Locate the primary option.
    const options = est.options || [];
    let primary = null;
    if (est.primary_option_id) {
      primary = options.find((o) => o.id === est.primary_option_id) || null;
    }
    if (!primary) primary = options.find((o) => o.is_primary) || null;
    if (!primary && options.length) primary = options[0];

    let weekly = 0;
    let monthly = 0;
    let source = "estimate";
    if (primary) {
      const d = Number(primary.days_per_week || 0);
      const h = Number(primary.hours_per_day || 0);
      if (d > 0 && h > 0) {
        weekly = +(d * h).toFixed(2);
        monthly = +(weekly * 4.33).toFixed(2);
        source = "estimate-primary";
      } else {
        weekly = Number(primary.weekly_hours || 0);
        monthly = Number(primary.monthly_hours || 0);
      }
    }
    // Safety net: Interna services must be ≥ 168h/sem.
    if (isInterna && weekly < 168) {
      weekly = 168.0;
      monthly = +(168.0 * 4.33).toFixed(2);
      source = "interna24x7";
    }
    // Ultimate fallback: estimate top-level.
    if (weekly <= 0 && monthly <= 0) {
      weekly = Number(est.weekly_hours || 0);
      monthly = Number(est.monthly_hours || 0);
    }
    return { monthly, weekly, source };
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-6 lg:p-8" data-testid="recruitment-panel">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-2">
            Requisição de Recrutamento (RH)
          </div>
          <h3 className="font-ui text-lg inline-flex items-center gap-2">
            <Users2 className="w-5 h-5 text-foreground" />
            Pipeline para o RH
          </h3>
        </div>
        {req?.status === "sent" && (
          <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-semibold px-3 py-1.5 rounded-full border bg-[hsl(var(--success-bg))] border-[hsl(var(--success))]/40 text-[hsl(var(--success))]" data-testid="recruitment-status-badge">
            <CheckCircle2 className="w-3.5 h-3.5" /> Enviada — {new Date(req.sent_at).toLocaleDateString("pt-PT")}
          </span>
        )}
      </div>

      {/* Preconditions */}
      <div className="flex flex-wrap gap-2 mb-5" data-testid="recruitment-preconditions">
        <Precondition ok={preAccepted} label="Contrato aceite" testid="pre-accepted" />
        <Precondition ok={preContract} label="Dados de contrato" testid="pre-contract" />
        <Precondition ok={preDeposit}  label={`Arranque caução ${preDeposit ? `· ${(deposit.kickstart_paid_amount||0).toFixed(0)}€` : ""}`} testid="pre-deposit" />
      </div>

      {!preDeposit ? (
        <div className="rounded-xl border border-[hsl(var(--warning))]/25 bg-[hsl(var(--warning-bg))] p-4 mb-4 text-sm text-[hsl(var(--warning))]" data-testid="deposit-waiting-daf">
          <b>Aguarda confirmação do DAF.</b> O recrutamento desbloqueia assim que o DAF confirmar o valor de arranque no portal exclusivo. Os pagamentos são confirmados em <code>/daf/&lt;token&gt;</code>, não aqui na app.
        </div>
      ) : (
        <div className="rounded-xl border border-[hsl(var(--success))]/25 bg-[hsl(var(--success-bg))] p-4 mb-4 text-sm text-[hsl(var(--success))]" data-testid="deposit-unlocked">
          <b>Arranque de recrutamento desbloqueado.</b> DAF confirmou {(deposit.kickstart_paid_amount||0).toFixed(2)}€ do valor de arranque de {(deposit.kickstart_target||0).toFixed(2)}€.
        </div>
      )}

      {/* Overrun banner — shows the state of the rui.pinto approval for
          AFs cost above the amount authorized on the proposal.
          Fetched by ``GET /leads/{id}/recruitment-overrun``. */}
      {overrunState && (overrunState.current_overrun || overrunState.latest_authorization) && (() => {
        const auth = overrunState.latest_authorization || {};
        const overrun = overrunState.current_overrun;
        const eur = (v) => `${Number(v || 0).toLocaleString("pt-PT", {minimumFractionDigits: 2})}€`;
        // 27/02/2026 — Approved authorization covers the current snapshot when
        // the numbers still match. The current_overrun is still returned by
        // the backend for display, but the approval remains valid so we show
        // the green "approved" banner and let the Send button through.
        const overrunTolerance = 0.5;
        const authorizedMatches =
          overrun && auth.status === "approved" &&
          Math.abs((auth.actual_af_cost ?? 0) - (overrun.actual ?? 0)) <= overrunTolerance &&
          Math.abs((auth.authorized_af_cost ?? 0) - (overrun.authorized ?? 0)) <= overrunTolerance;
        const approvedNoOverrun = auth.status === "approved" && !overrun;
        if (approvedNoOverrun || authorizedMatches) {
          return (
            <div className="rounded-xl border border-[hsl(var(--success))]/40 bg-[hsl(var(--success-bg))] p-4 mb-4" data-testid="recruitment-overrun-approved">
              <div className="flex items-start gap-2 text-sm text-[hsl(var(--success))]">
                <CheckCircle2 className="w-4 h-4 mt-0.5" />
                <div>
                  <b>Excesso aprovado.</b> {auth.decided_by_name || auth.decided_by_email} autorizou {eur(auth.actual_af_cost)} (autorizado na proposta: {eur(auth.authorized_af_cost)}). Podes enviar a requisição.
                </div>
              </div>
            </div>
          );
        }
        // Pending or overrun with no approval covering it → yellow banner.
        if (auth.status === "pending" || overrun) {
          const showReason = auth.status === "pending";
          const excess = overrun?.excess ?? auth.excess_amount ?? 0;
          const excessPct = overrun?.excess_pct ?? auth.excess_pct ?? 0;
          const actual = overrun?.actual ?? auth.actual_af_cost ?? 0;
          const authorized = overrun?.authorized ?? auth.authorized_af_cost ?? 0;
          return (
            <div className="rounded-xl border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning-bg))] p-4 mb-4 space-y-3" data-testid="recruitment-overrun-pending">
              <div className="flex items-start gap-2 text-sm text-[hsl(var(--warning))]">
                <AlertTriangle className="w-4 h-4 mt-0.5" />
                <div>
                  <b>Custo dos AFs alocados excede o valor autorizado na proposta.</b>
                  <div className="mt-1 text-[13px] text-foreground/90">
                    Autorizado: <b>{eur(authorized)}</b> · Alocado: <b>{eur(actual)}</b> · Excesso: <b>{eur(excess)} ({Number(excessPct).toFixed(1).replace(".", ",")}%)</b>
                  </div>
                  <div className="mt-2 text-[13px] text-foreground/80">
                    {showReason
                      ? <>Pedido enviado a <b>{overrunState.approver_email || auth.approver_email || "rui.pinto@caring.pt"}</b>. Estado: <span className="inline-flex items-center gap-1 font-semibold"><Clock className="w-3 h-3" /> pendente</span>. A requisição NÃO pode ser enviada ao RH enquanto não for aprovada.</>
                      : <>Ao guardar, será enviado pedido de aprovação a <b>{overrunState.approver_email || "rui.pinto@caring.pt"}</b>.</>
                    }
                  </div>
                </div>
              </div>
              {showReason && (
                <div className="border-t border-[hsl(var(--warning))]/25 pt-3">
                  <label className="block text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">
                    Motivo do excesso (visível ao aprovador)
                  </label>
                  <textarea
                    value={overrunReason}
                    onChange={(e) => setOverrunReason(e.target.value)}
                    rows={2}
                    placeholder="Ex: candidato experiente com €/h superior, região com escassez, fidelização estratégica…"
                    data-testid="overrun-reason-textarea"
                    className="w-full px-3 py-2 border border-border rounded-md text-[13px] resize-none bg-card focus:outline-none focus:ring-2 focus:ring-primary/25"
                  />
                  <div className="flex justify-end mt-2">
                    <button
                      onClick={saveOverrunReason}
                      disabled={savingReason || !overrunReason.trim()}
                      data-testid="overrun-reason-save"
                      className="inline-flex items-center gap-1.5 text-xs bg-foreground text-background hover:bg-[#1E293B] rounded-full px-3 py-1.5 disabled:opacity-40"
                    >
                      {savingReason ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                      Guardar motivo
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        }
        // Rejected: red banner.
        if (auth.status === "rejected") {
          return (
            <div className="rounded-xl border border-[hsl(var(--danger))]/40 bg-[hsl(var(--danger-bg))] p-4 mb-4" data-testid="recruitment-overrun-rejected">
              <div className="flex items-start gap-2 text-sm text-[hsl(var(--danger))]">
                <ShieldAlert className="w-4 h-4 mt-0.5" />
                <div>
                  <b>Excesso rejeitado</b> por {auth.decided_by_name || auth.decided_by_email}. Reduz o €/h dos ajudantes ou pede nova simulação de margem ao gestor.
                  {auth.decision_reason && <div className="mt-1 italic">"{auth.decision_reason}"</div>}
                </div>
              </div>
            </div>
          );
        }
        return null;
      })()}

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2 pt-1">
        <button
          onClick={openForm}
          disabled={!readyToGen || !canOperate}
          className="inline-flex items-center gap-1.5 text-xs bg-foreground hover:bg-slate-800 text-background rounded-full px-4 py-2 font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="recruitment-open-btn"
        >
          <FileText className="w-3.5 h-3.5" /> {req ? "Rever requisição" : "Gerar requisição RH"}
        </button>
        {req && (
          <>
            <a
              href={pdfHref} onClick={openPdf}
              className="inline-flex items-center gap-1.5 text-xs bg-muted hover:bg-muted border border-border rounded-full px-3 py-2 cursor-pointer"
              data-testid="recruitment-pdf-link"
            >
              <Download className="w-3.5 h-3.5" /> PDF
            </a>
            {req.status !== "sent" && canOperate && (() => {
              const auth = overrunState?.latest_authorization;
              const overrun = overrunState?.current_overrun;
              const overrunTolerance = 0.5;
              const covered = !!overrun && auth?.status === "approved" &&
                Math.abs((auth.actual_af_cost ?? 0) - (overrun.actual ?? 0)) <= overrunTolerance &&
                Math.abs((auth.authorized_af_cost ?? 0) - (overrun.authorized ?? 0)) <= overrunTolerance;
              const blocked = !!overrun && !covered;
              return (
                <button
                  onClick={sendToHR}
                  disabled={sending || blocked}
                  title={blocked ? "Aguarda aprovação do excesso de custo AFs pelo aprovador." : ""}
                  className="inline-flex items-center gap-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-primary-foreground rounded-full px-4 py-2 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="recruitment-send-btn"
                >
                  {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  Enviar ao RH
                </button>
              );
            })()}
          </>
        )}
      </div>

      {/* Requisition editor */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-4xl bg-card max-h-[90vh] overflow-y-auto" data-testid="recruitment-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl inline-flex items-center gap-2">
              <Users2 className="w-6 h-6 text-foreground" /> Requisição de Recrutamento
            </DialogTitle>
            <DialogDescription>Reveja e complete o dossier antes de o enviar ao RH.</DialogDescription>
          </DialogHeader>

          {prefilling && !form && (
            <div className="py-12 text-center text-muted-foreground flex flex-col items-center gap-2" data-testid="recruitment-prefilling">
              <Loader2 className="w-6 h-6 animate-spin text-foreground" />
              A IA está a preencher a requisição com base no lead…
            </div>
          )}

          {!prefilling && !form && (
            <div className="py-10 text-center flex flex-col items-center gap-3" data-testid="recruitment-empty">
              <AlertTriangle className="w-8 h-8 text-[hsl(var(--warning))]" strokeWidth={1.6} />
              <div>
                <div className="font-heading text-base text-foreground">Pré-preenchimento não disponível</div>
                <div className="text-[13px] text-muted-foreground mt-1 max-w-md">
                  A IA demorou demasiado ou falhou. Podes tentar de novo — se persistir, clica em "Preencher só com dados do lead" para arrancar sem sugestões.
                </div>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <button
                  onClick={openForm}
                  data-testid="recruitment-retry-prefill"
                  className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 inline-flex items-center gap-1.5"
                >
                  <RotateCcw className="w-3.5 h-3.5" /> Tentar novamente
                </button>
              </div>
            </div>
          )}

          {form && (
            <div className="space-y-3" data-testid="recruitment-form">
              <div className="flex items-center justify-end">
                <button onClick={regenerateAI} disabled={prefilling}
                  className="inline-flex items-center gap-1 text-[11px] text-foreground hover:text-[#6B4E2F] disabled:opacity-40"
                  data-testid="recruitment-regen-ai">
                  {prefilling ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                  Regenerar com IA
                </button>
              </div>

              {/* 1. General */}
              <Section title="1. Informação Geral" open={openSections.general}
                onToggle={() => setOpenSections((s) => ({ ...s, general: !s.general }))}
                testid="section-general">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Data" type="date" value={form.general?.date} onChange={(v) => set("general.date", v)} testid="g-date" />
                  <Field label="Unidade / Área" value={form.general?.unit_area} onChange={(v) => set("general.unit_area", v)} testid="g-unit" />
                  <Field label="Coordenador(a)" value={form.general?.coordinator} onChange={(v) => set("general.coordinator", v)} testid="g-coord" />
                  <Field label="Contacto" value={form.general?.contact} onChange={(v) => set("general.contact", v)} testid="g-contact" />
                  <Field label="Cliente / Serviço" value={form.general?.client_service} onChange={(v) => set("general.client_service", v)} testid="g-client" />
                  <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-1">Tipo de pedido</div>
                    <select value={form.general?.request_type || "Nova vaga"} onChange={(e) => set("general.request_type", e.target.value)}
                      className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm" data-testid="g-request-type">
                      <option>Nova vaga</option><option>Substituição</option><option>Reforço</option>
                    </select>
                  </label>
                  <Field label="Motivo" value={form.general?.motive} onChange={(v) => set("general.motive", v)} testid="g-motive" />
                  <Field label="Data prevista de início" type="date" value={form.general?.expected_start_date} onChange={(v) => set("general.expected_start_date", v)} testid="g-start" />
                  <Field label="Prazo para shortlist" type="date" value={form.general?.shortlist_deadline} onChange={(v) => set("general.shortlist_deadline", v)} testid="g-shortlist" />
                </div>
                <div className="mt-3">
                  <Field label="Justificação / Enquadramento" multiline rows={4}
                    value={form.general?.justification}
                    onChange={(v) => set("general.justification", v)}
                    testid="g-justification" />
                </div>
              </Section>

              {/* 2. Profile — bloco realçado, é a secção MAIS importante da requisição */}
              <div className="rounded-2xl border-2 border-primary bg-muted/40 p-1 my-3" data-testid="profile-highlight-wrap">
                <Section title="2. Perfil do Ajudante Familiar Pretendido" open={openSections.profile}
                  onToggle={() => setOpenSections((s) => ({ ...s, profile: !s.profile }))}
                  testid="section-profile">
                  <div className="text-xs text-foreground italic mb-3">
                    O que o RH precisa para procurar o candidato certo. Preenche com clareza — este é o coração do dossier.
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <label className="block">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-1">Função / Categoria</div>
                      <select value={form.profile?.role_category || "AF"} onChange={(e) => set("profile.role_category", e.target.value)}
                        className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm" data-testid="p-role">
                        <option value="AF">Ajudante Familiar (AF)</option>
                        <option value="AE">Ajudante Especializado (AE)</option>
                        <option value="Enf">Enfermeiro(a)</option>
                      </select>
                    </label>
                    <Field label="N.º de profissionais" type="number" value={form.profile?.num_professionals} onChange={(v) => set("profile.num_professionals", v || 1)} testid="p-num" />
                    <Field label="Habilitações mínimas" multiline value={form.profile?.min_qualifications} onChange={(v) => set("profile.min_qualifications", v)} testid="p-qual" />
                    <Field label="Experiência exigida" multiline value={form.profile?.required_experience} onChange={(v) => set("profile.required_experience", v)} testid="p-exp" />
                    <Field label="Certificações / Registos" value={form.profile?.certifications} onChange={(v) => set("profile.certifications", v)} testid="p-certs" />
                    <Field label="Competências comportamentais" multiline value={form.profile?.behavioral_competencies} onChange={(v) => set("profile.behavioral_competencies", v)} testid="p-comp" />
                    <div className="md:col-span-2">
                      <Field label="Critérios de exclusão" multiline value={form.profile?.exclusion_criteria} onChange={(v) => set("profile.exclusion_criteria", v)} testid="p-excl" />
                    </div>
                  </div>
                </Section>
              </div>

              {/* 3. Service org */}
              <Section title="3. Organização do Serviço" open={openSections.service}
                onToggle={() => setOpenSections((s) => ({ ...s, service: !s.service }))}
                testid="section-service">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Morada" value={form.service_organization?.address} onChange={(v) => set("service_organization.address", v)} testid="s-address" />
                  <Field label="Mobilidade" value={form.service_organization?.mobility} onChange={(v) => set("service_organization.mobility", v)} testid="s-mob" />
                  <Field label="Turnos / Escala" value={form.service_organization?.shifts} onChange={(v) => set("service_organization.shifts", v)} testid="s-shifts" />
                  <Field label="Noites" value={form.service_organization?.nights_active} onChange={(v) => set("service_organization.nights_active", v)} testid="s-nights" />
                  <Field label="Horas semanais" value={form.service_organization?.weekly_hours} onChange={(v) => set("service_organization.weekly_hours", v)} testid="s-hours" />
                  <Field label="Folgas" value={form.service_organization?.days_off} onChange={(v) => set("service_organization.days_off", v)} testid="s-days-off" />
                </div>
                <div className="mt-4">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-2">Quadro de horários</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {DAYS.map((d, i) => {
                      const row = form.service_organization?.schedule?.[i] || { day: d, hours: "", shift: "Dia", active: true };
                      return (
                        <div key={d} className="flex items-center gap-2 bg-muted/40 rounded-lg px-3 py-2 border border-border" data-testid={`sched-${d}`}>
                          <span className="w-9 text-xs font-semibold text-foreground">{d}</span>
                          <input value={row.hours} onChange={(e) => scheduleAt(i, "hours", e.target.value)}
                            placeholder="ex: 9h-19h" className="flex-1 bg-card border border-border rounded px-2 py-1 text-xs" />
                          <select value={row.shift} onChange={(e) => scheduleAt(i, "shift", e.target.value)}
                            className="bg-card border border-border rounded px-2 py-1 text-xs">
                            <option>Dia</option><option>Noite</option>
                          </select>
                          <label className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                            <input type="checkbox" checked={!!row.active} onChange={(e) => scheduleAt(i, "active", e.target.checked)} />
                            Ativa
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </Section>

              {/* 4. Contexto do serviço para o Ajudante — só o essencial */}
              <Section title="4. Contexto do Serviço (para o Ajudante)" open={openSections.client}
                onToggle={() => setOpenSections((s) => ({ ...s, client: !s.client }))}
                testid="section-client">
                <div className="text-xs text-muted-foreground bg-muted/40 border border-border rounded-lg p-3 mb-3">
                  Foca-te no que o AF precisa de saber antes de aceitar o serviço. Os detalhes clínicos completos
                  ficam no dossier interno do lead e não são enviados ao RH.
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Idade" value={form.client_user?.age} onChange={(v) => set("client_user.age", v)} testid="c-age" />
                  <Field label="Sexo" value={form.client_user?.sex} onChange={(v) => set("client_user.sex", v)} testid="c-sex" />
                  <Field label="Mobilidade / Transferências" multiline value={form.client_user?.mobility_transfers} onChange={(v) => set("client_user.mobility_transfers", v)} testid="c-mob" />
                  <Field label="Autocuidado" multiline value={form.client_user?.self_care} onChange={(v) => set("client_user.self_care", v)} testid="c-selfcare" />
                  <Field label="Rotina diária" multiline value={form.client_user?.daily_routine} onChange={(v) => set("client_user.daily_routine", v)} testid="c-routine" />
                  <Field label="Riscos a ter em atenção" multiline value={form.client_user?.risks} onChange={(v) => set("client_user.risks", v)} testid="c-risks" />
                  <Field label="Restrições ao serviço" multiline value={form.client_user?.restrictions} onChange={(v) => set("client_user.restrictions", v)} testid="c-restrict" />
                </div>

                <div className="mt-4 rounded-lg bg-muted/40 border border-border p-3">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-foreground font-semibold mb-2">Casa &amp; contexto do local de trabalho</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label="Tipo de habitação" value={form.client_user?.house_context?.housing_type} onChange={(v) => set("client_user.house_context.housing_type", v)} testid="c-house-type" />
                    <label className="block">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-1">Elevador</div>
                      <select value={form.client_user?.house_context?.elevator || "Não"}
                        onChange={(e) => set("client_user.house_context.elevator", e.target.value)}
                        className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm" data-testid="c-elev">
                        <option>Sim</option><option>Não</option><option>N/A</option>
                      </select>
                    </label>
                    <Field label="Escadas (pisos)" value={form.client_user?.house_context?.stairs_floors} onChange={(v) => set("client_user.house_context.stairs_floors", v)} testid="c-stairs" />
                    <label className="block">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-1">Cama articulada</div>
                      <select value={form.client_user?.house_context?.articulated_bed || "Não"}
                        onChange={(e) => set("client_user.house_context.articulated_bed", e.target.value)}
                        className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm" data-testid="c-bed">
                        <option>Sim</option><option>Não</option>
                      </select>
                    </label>
                    <label className="block">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-1">WC adaptado</div>
                      <select value={form.client_user?.house_context?.adapted_wc || "Não"}
                        onChange={(e) => set("client_user.house_context.adapted_wc", e.target.value)}
                        className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm" data-testid="c-wc">
                        <option>Sim</option><option>Não</option>
                      </select>
                    </label>
                    <label className="block">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-1">Animais</div>
                      <select value={form.client_user?.house_context?.animals || "Não"}
                        onChange={(e) => set("client_user.house_context.animals", e.target.value)}
                        className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm" data-testid="c-animals">
                        <option>Sim</option><option>Não</option>
                      </select>
                    </label>
                    <label className="block">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-1">Fumadores</div>
                      <select value={form.client_user?.house_context?.smokers || "Não"}
                        onChange={(e) => set("client_user.house_context.smokers", e.target.value)}
                        className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm" data-testid="c-smokers">
                        <option>Sim</option><option>Não</option>
                      </select>
                    </label>
                  </div>
                </div>
              </Section>

              {/* 5. Helpers allocation */}
              <Section title="5. Alocação de Ajudantes Familiares" open={openSections.values}
                onToggle={() => setOpenSections((s) => ({ ...s, values: !s.values }))}
                testid="section-values">
                {(() => {
                  const target = Number(form.values?.client_monthly_hours || 0);
                  const weekly = Number(form.values?.weekly_hours_reference || 0);
                  const revenue = Number(form.values?.client_monthly_value || 0);
                  const helpers = form.values?.helpers || [];
                  // Live snapshot of what the current estimate expects. When the
                  // consultor edits the estimate after the requisition was
                  // prefilled, the stored target can go stale — we surface the
                  // divergence with a "Sincronizar" CTA below.
                  const expected = _deriveExpectedClientHours();
                  const estMonthly = Number(expected.monthly || 0);
                  const estWeekly = Number(expected.weekly || 0);
                  const targetOutOfSync = estMonthly > 0
                    && Math.abs(estMonthly - target) > 0.5;
                  const sum = helpers.reduce((acc, h) => acc + Number(h.monthly_hours || 0), 0);
                  const totalMonthlyCost = helpers.reduce(
                    (acc, h) => acc + Number(h.monthly_hours || 0) * Number(h.hourly_rate || 0),
                    0,
                  );
                  // 27/02/2026 — Nova regra: soma de horas AF >= horas cliente
                  // (podem estar acima; nunca abaixo).
                  const hoursDeficit = Math.max(0, target - sum);
                  const hoursSurplus = Math.max(0, sum - target);
                  const hoursMatch = sum + 0.5 >= target;
                  // Custo AFs autorizado (tecto da proposta). Custo pode estar
                  // até 10% abaixo do autorizado (poupança). Acima do autorizado
                  // é overrun → aprovação de rui.pinto (fluxo existente).
                  const afCostCeiling = Number(lead?.estimate?.af_monthly_cost || 0);
                  const costRatio = afCostCeiling > 0 ? totalMonthlyCost / afCostCeiling : null;
                  const costBand = costRatio == null ? "unknown"
                    : costRatio > 1.0 ? "over"     // acima do autorizado — overrun
                    : costRatio >= 0.9 ? "ok"      // 90–100% do autorizado — perfeito
                    : "under";                     // abaixo de 90% — sub-utilização
                  const margin = revenue > 0 ? (revenue - totalMonthlyCost) / revenue * 100 : null;
                  const marginBand = margin == null ? "unknown"
                    : margin < 33 ? "blocked"
                    : margin < 36 ? "warn"
                    : "great";
                  return (
                    <>
                      {targetOutOfSync && (
                        <div className="mb-3 rounded-lg border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning-bg))] p-3 flex items-start gap-2" data-testid="alloc-target-out-of-sync">
                          <AlertTriangle className="w-4 h-4 mt-0.5 text-[hsl(var(--warning))] shrink-0" />
                          <div className="flex-1 text-sm text-[hsl(var(--warning))]">
                            <b>As horas do cliente não batem certo com o serviço.</b>
                            <div className="text-[13px] text-foreground/90 mt-1">
                              Requisição: <b>{target.toFixed(2)}h/mês</b> · Esperado: <b>{estMonthly.toFixed(2)}h/mês</b> ({estWeekly.toFixed(1)}h/sem
                              {expected.source === "interna24x7" && " · Interna 24×7"}
                              {expected.source === "estimate-primary" && " · opção primária da estimativa"}
                              {expected.source === "estimate" && " · da estimativa"}).
                            </div>
                            <button
                              type="button"
                              data-testid="alloc-sync-with-estimate"
                              onClick={() => {
                                const est = lead?.estimate || {};
                                const revenueFromEst = Number(lead?.proposal?.monthly_price_max || est?.options?.find?.(o => o.is_primary || o.id === est.primary_option_id)?.monthly_avg || 0);
                                set("values.client_monthly_hours", Number(estMonthly.toFixed(2)));
                                set("values.weekly_hours_reference", Number(estWeekly.toFixed(2)));
                                if (revenueFromEst > 0) set("values.client_monthly_value", Number(revenueFromEst.toFixed(2)));
                                toast.success(`Horas do cliente sincronizadas para ${estMonthly.toFixed(2)}h/mês. Redistribui os AFs para o novo total.`);
                              }}
                              className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold bg-[hsl(var(--warning))] text-white hover:opacity-90 rounded-full px-3 py-1.5"
                            >
                              <RotateCcw className="w-3.5 h-3.5" />
                              Corrigir para {estMonthly.toFixed(2)}h
                            </button>
                          </div>
                        </div>
                      )}
                      {/* Header — totals */}
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
                        <div className="bg-muted/40 border border-border rounded-lg p-3">
                          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Horas / mês</div>
                          <div className="flex items-center gap-1 mt-1">
                            <input
                              type="number"
                              step="0.1"
                              min={0}
                              value={target}
                              onChange={(e) => set("values.client_monthly_hours", Number(e.target.value))}
                              data-testid="alloc-target-input"
                              className="w-full bg-transparent font-ui text-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 rounded px-1 tabular-nums"
                            />
                            <span className="text-lg text-muted-foreground">h</span>
                          </div>
                          <div className="text-[10px] text-muted-foreground mt-0.5" data-testid="alloc-target">
                            {target.toFixed(1)}h · Semanal ref.: {weekly.toFixed(1)}h
                          </div>
                        </div>
                        <div className={`rounded-lg p-3 border ${
                          hoursMatch ? "bg-[hsl(var(--success-bg))] border-[hsl(var(--success))]/25" : "bg-[hsl(var(--warning-bg))] border-[hsl(var(--warning))]/25"
                        }`}>
                          <div className={`text-[10px] uppercase tracking-[0.18em] font-semibold ${
                            hoursMatch ? "text-[hsl(var(--success))]" : "text-[hsl(var(--warning))]"
                          }`}>Soma alocada</div>
                          <div className={`font-ui text-lg mt-1 ${
                            hoursMatch ? "text-[hsl(var(--success))]" : "text-[hsl(var(--warning))]"
                          }`} data-testid="alloc-sum">
                            {sum.toFixed(1)}h{" "}
                            <span className="text-xs font-normal">
                              {hoursDeficit > 0
                                ? `· faltam ${hoursDeficit.toFixed(1)}h`
                                : hoursSurplus > 0.5
                                  ? `· +${hoursSurplus.toFixed(1)}h de folga`
                                  : "· OK"}
                            </span>
                          </div>
                        </div>
                        <div className="rounded-lg p-3 border bg-muted/40 border-primary/40" data-testid="alloc-revenue">
                          <div className="text-[10px] uppercase tracking-[0.18em] text-foreground font-semibold">Receita cliente / mês</div>
                          <div className="font-ui text-lg text-foreground mt-1 tabular-nums">
                            {revenue.toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€
                          </div>
                        </div>
                        <div
                          className={`rounded-lg p-3 border ${
                            costBand === "over" ? "bg-[hsl(var(--danger-bg))] border-[hsl(var(--danger))]/40"
                            : costBand === "under" ? "bg-[hsl(var(--warning-bg))] border-[hsl(var(--warning))]/25"
                            : costBand === "ok" ? "bg-[hsl(var(--success-bg))] border-[hsl(var(--success))]/25"
                            : "bg-muted/40 border-border"
                          }`}
                          data-testid="alloc-total-cost"
                          data-cost-band={costBand}
                        >
                          <div className={`text-[10px] uppercase tracking-[0.18em] font-semibold ${
                            costBand === "over" ? "text-[hsl(var(--danger))]"
                            : costBand === "under" ? "text-[hsl(var(--warning))]"
                            : costBand === "ok" ? "text-[hsl(var(--success))]"
                            : "text-muted-foreground"
                          }`}>Custo AF / mês</div>
                          <div className={`font-ui text-lg mt-1 tabular-nums font-semibold ${
                            costBand === "over" ? "text-[hsl(var(--danger))]"
                            : costBand === "under" ? "text-[hsl(var(--warning))]"
                            : costBand === "ok" ? "text-[hsl(var(--success))]"
                            : "text-foreground"
                          }`}>
                            {totalMonthlyCost.toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€
                          </div>
                          {afCostCeiling > 0 ? (
                            <div className={`text-[10px] mt-0.5 ${
                              costBand === "over" ? "text-[hsl(var(--danger))]"
                              : costBand === "under" ? "text-[hsl(var(--warning))]"
                              : costBand === "ok" ? "text-[hsl(var(--success))]"
                              : "text-muted-foreground"
                            }`}>
                              tecto autorizado: {afCostCeiling.toLocaleString("pt-PT", {minimumFractionDigits: 2, maximumFractionDigits: 2})}€
                              {costRatio != null && ` · ${(costRatio*100).toFixed(0)}%`}
                            </div>
                          ) : (
                            revenue > 0 && (
                              <div className="text-[10px] mt-0.5 text-muted-foreground">
                                custo aceitável (33% margem): até {(revenue * 0.67).toLocaleString("pt-PT", {minimumFractionDigits: 2, maximumFractionDigits: 2})}€
                              </div>
                            )
                          )}
                        </div>
                        <div
                          className={`rounded-lg p-3 border-2 ${
                            marginBand === "blocked" ? "bg-[hsl(var(--danger-bg))] border-rose-400"
                            : marginBand === "warn" ? "bg-[hsl(var(--warning-bg))] border-amber-400"
                            : marginBand === "great" ? "bg-[hsl(var(--success-bg))] border-emerald-500"
                            : "bg-muted/40 border-border"
                          }`}
                          data-testid="alloc-margin"
                          data-margin-band={marginBand}
                        >
                          <div className={`text-[10px] uppercase tracking-[0.18em] font-semibold ${
                            marginBand === "blocked" ? "text-[hsl(var(--danger))]"
                            : marginBand === "warn" ? "text-[hsl(var(--warning))]"
                            : marginBand === "great" ? "text-[hsl(var(--success))]"
                            : "text-muted-foreground"
                          }`}>Margem</div>
                          <div className={`font-ui text-xl mt-1 tabular-nums font-semibold ${
                            marginBand === "blocked" ? "text-[hsl(var(--danger))]"
                            : marginBand === "warn" ? "text-[hsl(var(--warning))]"
                            : marginBand === "great" ? "text-[hsl(var(--success))]"
                            : "text-foreground"
                          }`} data-testid="alloc-margin-pct">
                            {margin == null ? "—" : `${margin.toFixed(1)}%`}
                          </div>
                          <div className={`text-[10px] mt-0.5 ${
                            marginBand === "blocked" ? "text-[hsl(var(--danger))]"
                            : marginBand === "warn" ? "text-[hsl(var(--warning))]"
                            : marginBand === "great" ? "text-[hsl(var(--success))]"
                            : "text-muted-foreground"
                          }`}>
                            {marginBand === "blocked" && "Bloqueado — mínimo 33%"}
                            {marginBand === "warn" && "Aceitável (33–36%)"}
                            {marginBand === "great" && "🎉 Parabéns — margem sã"}
                            {marginBand === "unknown" && "Sem receita definida"}
                          </div>
                        </div>
                      </div>

                      {marginBand === "great" && (
                        <div className="mb-3 rounded-lg bg-[hsl(var(--success-bg))] border border-[hsl(var(--success))]/40 text-[hsl(var(--success))] p-3 text-sm" data-testid="alloc-margin-celebrate">
                          <b>🎉 Excelente negociação!</b> Com {margin.toFixed(1)}% de margem estás acima do alvo saudável de 36%. Este contrato é rentável e sustentável.
                        </div>
                      )}
                      {marginBand === "blocked" && (() => {
                        const latestMarginAuth = marginAuthState?.latest;
                        return (
                        <div className="mb-3 rounded-lg bg-[hsl(var(--danger-bg))] border border-[hsl(var(--danger))]/40 p-3 text-sm space-y-3" data-testid="alloc-margin-blocked">
                          <div className="text-[hsl(var(--danger))]">
                            <b>Margem abaixo do mínimo permitido (33%).</b> Com {margin.toFixed(1)}% este contrato não é sustentável. Reduza os valores/hora dos AF, renegoceie o preço com o cliente ou reveja o volume de horas antes de continuar.
                          </div>
                          {revenue > 0 && (
                            <div className="text-xs text-foreground/80 bg-card border border-border rounded p-2 tabular-nums">
                              Custo aceitável (mínimo 33% margem): até <b>{(revenue * 0.67).toLocaleString("pt-PT", {minimumFractionDigits: 2, maximumFractionDigits: 2})}€/mês</b>
                              {totalMonthlyCost > 0 && ` — actualmente ${totalMonthlyCost.toLocaleString("pt-PT", {minimumFractionDigits: 2})}€ (excesso de ${(totalMonthlyCost - revenue * 0.67).toLocaleString("pt-PT", {minimumFractionDigits: 2})}€)`}
                            </div>
                          )}
                          {/* Show latest margin authorization state, if any */}
                          {latestMarginAuth?.status === "pending" && (
                            <div className="text-xs bg-[hsl(var(--warning-bg))] border border-[hsl(var(--warning))]/30 text-[hsl(var(--warning))] rounded p-2" data-testid="margin-auth-pending">
                              Pedido enviado a <b>{latestMarginAuth.approver_email}</b> — aguarda decisão (a receber por email).
                            </div>
                          )}
                          {latestMarginAuth?.status === "approved" && (
                            <div className="text-xs bg-[hsl(var(--success-bg))] border border-[hsl(var(--success))]/30 text-[hsl(var(--success))] rounded p-2" data-testid="margin-auth-approved">
                              <b>Aprovado</b> por {latestMarginAuth.decided_by_name || latestMarginAuth.decided_by_email}. Podes guardar/enviar.
                            </div>
                          )}
                          {latestMarginAuth?.status === "edited" && (
                            <div className="text-xs bg-[hsl(var(--info-bg))] border border-[hsl(var(--info))]/30 text-[hsl(var(--info))] rounded p-2" data-testid="margin-auth-edited">
                              <b>Sugestão de novos valores:</b> receita {Number(latestMarginAuth.suggested_revenue||0).toLocaleString("pt-PT", {minimumFractionDigits:2})}€ · custo AFs {Number(latestMarginAuth.suggested_af_cost||0).toLocaleString("pt-PT", {minimumFractionDigits:2})}€. Aplica-os manualmente antes de guardar.
                              {latestMarginAuth.decision_reason && <div className="italic mt-1">"{latestMarginAuth.decision_reason}"</div>}
                            </div>
                          )}
                          {latestMarginAuth?.status === "rejected" && (
                            <div className="text-xs bg-[hsl(var(--danger-bg))] border border-[hsl(var(--danger))]/30 text-[hsl(var(--danger))] rounded p-2" data-testid="margin-auth-rejected">
                              <b>Rejeitado</b> por {latestMarginAuth.decided_by_name || latestMarginAuth.decided_by_email}. Renegoceie o valor ou reveja o custo.
                              {latestMarginAuth.decision_reason && <div className="italic mt-1">"{latestMarginAuth.decision_reason}"</div>}
                            </div>
                          )}
                          {/* Request-approval CTA — only when there's no pending request */}
                          {latestMarginAuth?.status !== "pending" && !showMarginRequest && (
                            <button
                              type="button"
                              onClick={() => setShowMarginRequest(true)}
                              data-testid="btn-request-margin-auth"
                              className="inline-flex items-center gap-1.5 text-xs font-semibold bg-foreground text-background hover:bg-[#1E293B] rounded-full px-4 py-1.5"
                            >
                              <ShieldAlert className="w-3.5 h-3.5" />
                              Pedir autorização a caring@caring.pt
                            </button>
                          )}
                          {showMarginRequest && (
                            <div className="bg-card border border-border rounded p-3 space-y-2" data-testid="margin-request-form">
                              <label className="block text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                                Motivo para o aprovador (obrigatório)
                              </label>
                              <textarea
                                value={marginReason}
                                onChange={(e) => setMarginReason(e.target.value)}
                                rows={3}
                                placeholder="Ex: contrato estratégico com potencial de upsell, cliente com múltiplos familiares, região crítica…"
                                data-testid="margin-request-reason"
                                className="w-full px-3 py-2 border border-border rounded-md text-[13px] resize-none bg-card focus:outline-none focus:ring-2 focus:ring-primary/25"
                              />
                              <div className="text-[11px] text-muted-foreground">
                                Aprovador recebe: receita {revenue.toLocaleString("pt-PT", {minimumFractionDigits: 2})}€ · custo AFs {totalMonthlyCost.toLocaleString("pt-PT", {minimumFractionDigits: 2})}€ · margem {margin.toFixed(1)}%.
                              </div>
                              <div className="flex justify-end gap-2">
                                <button
                                  type="button"
                                  onClick={() => { setShowMarginRequest(false); setMarginReason(""); }}
                                  className="text-xs px-3 py-1.5 rounded-full border border-border bg-card hover:bg-muted"
                                >Cancelar</button>
                                <button
                                  type="button"
                                  onClick={submitMarginAuthRequest}
                                  disabled={submittingMarginReq || !marginReason.trim()}
                                  data-testid="margin-request-submit"
                                  className="inline-flex items-center gap-1.5 text-xs font-semibold bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-4 py-1.5 disabled:opacity-40"
                                >
                                  {submittingMarginReq ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                                  Enviar pedido
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                        );
                      })()}

                      {/* Actions row */}
                      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
                          Ajudantes Familiares (máx. 4)
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <button onClick={autofillHourlyRates}
                            disabled={!helpers.length || target <= 0 || !Number(lead?.estimate?.af_monthly_cost || 0)}
                            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-medium bg-card border border-border text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
                            title={!Number(lead?.estimate?.af_monthly_cost || 0)
                              ? "Indica primeiro o custo AFs autorizado no Simulador de Margem."
                              : "Preenche o €/h de todos os AFs = custo autorizado ÷ horas do cliente"}
                            data-testid="alloc-autofill-rates">
                            <Sparkles className="w-3.5 h-3.5" /> Preencher €/h auto
                          </button>
                          <button onClick={distributeEvenly}
                            disabled={!helpers.length || target <= 0}
                            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-medium bg-card border border-border text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid="alloc-distribute">
                            Distribuir uniformemente
                          </button>
                          <button onClick={addHelper}
                            disabled={helpers.length >= 4}
                            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-semibold bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid="alloc-add">
                            <Plus className="w-3.5 h-3.5" /> Adicionar ajudante
                          </button>
                        </div>
                      </div>

                      {/* Helpers grid */}
                      {helpers.length === 0 && (
                        <div className="text-xs text-muted-foreground bg-muted/40 border border-dashed border-border rounded-lg p-4 text-center" data-testid="alloc-empty">
                          Sem ajudantes atribuídos. Adicione pelo menos um para cobrir as {target.toFixed(1)}h mensais do cliente.
                        </div>
                      )}
                      {/* Rules panel — new UX (27/02/2026) */}
                      {helpers.length > 0 && (
                        <div className="mb-4 rounded-lg border border-border bg-muted/30 p-3 text-[11.5px] text-muted-foreground leading-relaxed" data-testid="alloc-rules">
                          <div className="text-[10px] uppercase tracking-[0.15em] text-foreground font-semibold mb-1">Como preencher</div>
                          <ul className="list-disc pl-5 space-y-0.5">
                            <li>Introduz as <b>horas semanais</b> de cada AF — a média mensal (× 4,33) é calculada automaticamente.</li>
                            <li>Podes definir o <b>€/mês</b> ou o <b>€/h</b> — o outro valor é calculado automaticamente com base nas horas mensais.</li>
                            <li>A soma das horas dos AFs <b>não pode ser inferior</b> ao total mensal do cliente ({target.toFixed(1)}h).</li>
                            <li>O custo total <b>não pode exceder</b> o autorizado {afCostCeiling > 0 ? `(${afCostCeiling.toLocaleString("pt-PT", {minimumFractionDigits: 2})}€)` : ""}; pode ficar até <b>10% abaixo</b> (poupança) e ainda avançar.</li>
                          </ul>
                        </div>
                      )}
                      {helpers.map((h, i) => {
                        const helperWeekly = Number(h.weekly_hours || (Number(h.monthly_hours || 0) / 4.33) || 0);
                        const helperMonthly = helperWeekly > 0
                          ? +(helperWeekly * 4.33).toFixed(2)
                          : Number(h.monthly_hours || 0);
                        const hourly = Number(h.hourly_rate || 0);
                        const monthlyPay = +(helperMonthly * hourly).toFixed(2);
                        const afCostCeilingRow = Number(lead?.estimate?.af_monthly_cost || 0);
                        const suggestedRate = target > 0 && afCostCeilingRow > 0
                          ? Math.round((afCostCeilingRow / target) * 100) / 100
                          : null;
                        const applyRateWithMonthly = (rate, m) => {
                          setForm((f) => {
                            const next = structuredClone(f || {});
                            const arr = next.values?.helpers || [];
                            if (!arr[i]) return f;
                            arr[i].hourly_rate = Number(rate.toFixed(2));
                            arr[i].monthly_hours = Number(m.toFixed(2));
                            next.values.helpers = arr;
                            return next;
                          });
                        };
                        const onWeeklyChange = (w) => {
                          const newMonthly = +(Number(w) * 4.33).toFixed(2);
                          setForm((f) => {
                            const next = structuredClone(f || {});
                            const arr = next.values?.helpers || [];
                            if (!arr[i]) return f;
                            arr[i].weekly_hours = Number(w);
                            arr[i].monthly_hours = newMonthly;
                            next.values.helpers = arr;
                            return next;
                          });
                        };
                        const onHourlyChange = (rate) => setHelper(i, "hourly_rate", Number(rate));
                        const onMonthlyPayChange = (mp) => {
                          if (helperMonthly <= 0) {
                            toast.info("Define primeiro as horas semanais deste AF.");
                            return;
                          }
                          // 27/02/2026 — 4 decimais internamente para o valor
                          // total (€/mês) não perder cêntimos no round-trip.
                          const newRate = +(Number(mp) / helperMonthly).toFixed(4);
                          setHelper(i, "hourly_rate", newRate);
                        };
                        return (
                          <div key={i} className="grid grid-cols-12 gap-2 mb-4 items-end" data-testid={`alloc-row-${i}`}>
                            <div className="col-span-1 pb-1 text-[10px] uppercase tracking-[0.15em] text-foreground font-semibold">AF #{i + 1}</div>
                            <div className="col-span-1">
                              <div className="text-[9px] uppercase tracking-wider text-muted-foreground mb-0.5">Papel</div>
                              <select value={h.role || "AF"} onChange={(e) => setHelper(i, "role", e.target.value)}
                                className="w-full h-8 bg-card border border-border rounded px-2 text-xs"
                                data-testid={`alloc-role-${i}`}>
                                <option>AF</option><option>AE</option>
                              </select>
                            </div>
                            <div className="col-span-1">
                              <div className="text-[9px] uppercase tracking-wider text-muted-foreground mb-0.5">Turno</div>
                              <select value={h.shift || "Dia"} onChange={(e) => setHelper(i, "shift", e.target.value)}
                                className="w-full h-8 bg-card border border-border rounded px-2 text-xs"
                                data-testid={`alloc-shift-${i}`}>
                                <option>Dia</option><option>Noite</option><option>Misto</option>
                              </select>
                            </div>
                            <div className="col-span-2">
                              <div className="text-[9px] uppercase tracking-wider text-muted-foreground mb-0.5">Dias</div>
                              <input placeholder="ex: Seg-Sex" value={h.days || ""}
                                onChange={(e) => setHelper(i, "days", e.target.value)}
                                className="w-full h-8 bg-card border border-border rounded px-2 text-xs"
                                data-testid={`alloc-days-${i}`} />
                            </div>
                            <div className="col-span-2">
                              <div className="text-[9px] uppercase tracking-wider text-muted-foreground mb-0.5">Horas / semana</div>
                              <div className="relative">
                                <input type="number" step="0.5" min="0" max="168"
                                  value={helperWeekly || ""}
                                  onChange={(e) => onWeeklyChange(e.target.value)}
                                  placeholder="0"
                                  className="w-full h-8 bg-card border border-border rounded px-2 pr-6 text-xs text-right tabular-nums"
                                  data-testid={`alloc-weekly-${i}`} />
                                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground pointer-events-none">h</span>
                              </div>
                              <div className="text-[9px] text-muted-foreground/80 mt-0.5 tabular-nums" data-testid={`alloc-monthly-derived-${i}`}>
                                = {helperMonthly.toFixed(1)}h/mês
                              </div>
                            </div>
                            <div className="col-span-2">
                              <div className="text-[9px] uppercase tracking-wider text-muted-foreground mb-0.5">€/hora</div>
                              <div className="relative">
                                <input type="number" step="0.01" min="0"
                                  value={hourly ? Number(hourly.toFixed(4)) : ""}
                                  onChange={(e) => onHourlyChange(e.target.value)}
                                  placeholder="0,00"
                                  className="w-full h-8 bg-card border border-border rounded px-2 pr-6 text-xs text-right tabular-nums"
                                  data-testid={`alloc-rate-${i}`} />
                                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground pointer-events-none">€</span>
                              </div>
                              {suggestedRate != null && Math.abs(hourly - suggestedRate) > 0.005 && (
                                <button type="button"
                                  onClick={() => setHelper(i, "hourly_rate", suggestedRate)}
                                  data-testid={`alloc-rate-auto-${i}`}
                                  className="text-[9px] text-[hsl(var(--info))] hover:underline whitespace-nowrap mt-0.5"
                                  title={`Preencher com ${suggestedRate.toFixed(2)}€/h (custo autorizado ÷ horas)`}>
                                  auto: {suggestedRate.toFixed(2)}€
                                </button>
                              )}
                            </div>
                            <div className="col-span-2">
                              <div className="text-[9px] uppercase tracking-wider text-muted-foreground mb-0.5">€/mês AF</div>
                              <div className="relative">
                                <input type="number" step="1" min="0"
                                  value={monthlyPay ? Number(monthlyPay.toFixed(2)) : ""}
                                  onChange={(e) => onMonthlyPayChange(e.target.value)}
                                  placeholder="0,00"
                                  className="w-full h-8 bg-card border-2 border-primary/50 rounded px-2 pr-6 text-xs text-right tabular-nums font-semibold"
                                  data-testid={`alloc-monthly-pay-${i}`} />
                                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground pointer-events-none">€</span>
                              </div>
                              <div className="text-[9px] text-muted-foreground/80 mt-0.5">
                                = horas × €/h
                              </div>
                            </div>
                            <button onClick={() => removeHelper(i)}
                              className="col-span-1 text-muted-foreground/70 hover:text-rose-500 justify-self-center pb-2"
                              data-testid={`alloc-remove-${i}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                            {h.notes !== undefined && (
                              <input placeholder="Notas (opcional)" value={h.notes || ""}
                                onChange={(e) => setHelper(i, "notes", e.target.value)}
                                className="col-start-2 col-span-11 bg-card border border-border rounded px-2 py-1 text-xs mt-1"
                                data-testid={`alloc-notes-${i}`} />
                            )}
                          </div>
                        );
                      })}

                      {/* Save-time hint */}
                      {!hoursMatch && helpers.length > 0 && (
                        <div className="mt-2 text-xs text-[hsl(var(--warning))] bg-[hsl(var(--warning-bg))] border border-[hsl(var(--warning))]/25 rounded p-2" data-testid="alloc-warning">
                          Faltam {hoursDeficit.toFixed(1)}h para cobrir a média mensal do cliente ({target.toFixed(1)}h). Aumenta as horas semanais de um dos AFs antes de guardar.
                        </div>
                      )}
                      {costBand === "over" && helpers.length > 0 && (
                        <div className="mt-2 text-xs text-[hsl(var(--danger))] bg-[hsl(var(--danger-bg))] border border-[hsl(var(--danger))]/25 rounded p-2" data-testid="alloc-cost-over-warning">
                          Custo total ({totalMonthlyCost.toLocaleString("pt-PT", {minimumFractionDigits: 2})}€) acima do autorizado ({afCostCeiling.toLocaleString("pt-PT", {minimumFractionDigits: 2})}€). Ao guardar, será enviado pedido de aprovação a rui.pinto@caring.pt.
                        </div>
                      )}
                      {costBand === "under" && helpers.length > 0 && (
                        <div className="mt-2 text-xs text-[hsl(var(--warning))] bg-[hsl(var(--warning-bg))] border border-[hsl(var(--warning))]/25 rounded p-2" data-testid="alloc-cost-under-warning">
                          Custo total {((1 - costRatio)*100).toFixed(0)}% abaixo do autorizado. Poupança de {(afCostCeiling - totalMonthlyCost).toLocaleString("pt-PT", {minimumFractionDigits: 2})}€ — verifica se os €/h são realistas.
                        </div>
                      )}

                      {/* Medication schedule (retained) */}
                      <div className="mt-6">
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Plano de medicação (grelha)</div>
                          <button onClick={addMed} className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-muted border border-border hover:bg-muted" data-testid="med-add">
                            <Plus className="w-3 h-3" /> Adicionar
                          </button>
                        </div>
                        {(form.values?.medication_schedule || []).map((m, i) => (
                          <div key={i} className="grid grid-cols-12 gap-2 mb-2" data-testid={`med-row-${i}`}>
                            <input placeholder="Dose" value={m.dose || ""} onChange={(e) => setMed(i, "dose", e.target.value)} className="col-span-3 bg-card border border-border rounded px-2 py-1 text-xs" />
                            <input placeholder="Hora" value={m.hour || ""} onChange={(e) => setMed(i, "hour", e.target.value)} className="col-span-2 bg-card border border-border rounded px-2 py-1 text-xs" />
                            <input placeholder="Quem" value={m.who || ""} onChange={(e) => setMed(i, "who", e.target.value)} className="col-span-3 bg-card border border-border rounded px-2 py-1 text-xs" />
                            <input placeholder="Observações" value={m.obs || ""} onChange={(e) => setMed(i, "obs", e.target.value)} className="col-span-3 bg-card border border-border rounded px-2 py-1 text-xs" />
                            <button onClick={() => removeMed(i)} className="col-span-1 text-muted-foreground/70 hover:text-rose-500" data-testid={`med-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        ))}
                      </div>
                    </>
                  );
                })()}
              </Section>

              {/* 6. Observations */}
              <Section title="6. Observações e Enquadramento" open={openSections.obs}
                onToggle={() => setOpenSections((s) => ({ ...s, obs: !s.obs }))}
                testid="section-obs">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Enquadramento geral" multiline value={form.observations?.general_framework} onChange={(v) => set("observations.general_framework", v)} testid="o-frame" />
                  <Field label="Dependências / rotinas" multiline value={form.observations?.dependencies_routines} onChange={(v) => set("observations.dependencies_routines", v)} testid="o-dep" />
                  <Field label="Preferências do cliente / família" multiline value={form.observations?.client_family_preferences} onChange={(v) => set("observations.client_family_preferences", v)} testid="o-pref" />
                  <Field label="Condições do domicílio" multiline value={form.observations?.home_conditions} onChange={(v) => set("observations.home_conditions", v)} testid="o-home" />
                  <Field label="Riscos / Alertas" multiline value={form.observations?.risks_alerts} onChange={(v) => set("observations.risks_alerts", v)} testid="o-alerts" />
                </div>
              </Section>
            </div>
          )}

          <DialogFooter className="gap-2 pt-2">
            {(() => {
              const revenue = Number(form?.values?.client_monthly_value || 0);
              const cost = (form?.values?.helpers || []).reduce(
                (a, h) => a + Number(h.monthly_hours || 0) * Number(h.hourly_rate || 0), 0);
              const margin = revenue > 0 ? (revenue - cost) / revenue * 100 : null;
              const blocked = margin != null && margin < 33;
              return (
                <>
                  <button onClick={() => setFormOpen(false)} className="px-4 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:bg-muted" data-testid="recruitment-cancel">Fechar</button>
                  {blocked && (
                    <span className="text-xs text-[hsl(var(--danger))] self-center mr-2" data-testid="recruitment-blocked-hint">
                      Margem {margin.toFixed(1)}% &lt; 33% — impossível guardar.
                    </span>
                  )}
                  <button onClick={saveReq}
                    disabled={savingReq || !form || blocked}
                    className="px-5 py-2 rounded-lg bg-foreground hover:bg-slate-800 text-background text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                    title={blocked ? `Margem ${margin.toFixed(1)}% abaixo do mínimo de 33%` : ""}
                    data-testid="recruitment-save">
                    {savingReq ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                    Guardar
                  </button>
                </>
              );
            })()}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
